/**
 * DeferredHooks — non-critical initialization hooks moved out of AppLayout.
 *
 * S8: These hooks are deferred until after first paint to improve startup time.
 * They handle background sync, analytics, tracking, and other features that
 * don't need to run before the user sees the UI.
 */

import { useEffect } from "react"
import { useDeviceSync } from "@/app/hooks/useDeviceSync"
import { useReminderSync } from "@/features/reminders/useReminderSync"
import { useSharedTaskDateSync } from "@/features/shared-view-pref/useSharedTaskDateSync"
import { useSharedReminderSync } from "@/features/shared-view-pref/useSharedReminderSync"
import { usePushSync } from "@/features/reminders/usePushSync"
import { usePostLoginCleanup } from "@/app/hooks/usePostLoginCleanup"
import { useTokenSync } from "@/app/hooks/useTokenSync"
import { useAffiliateTracking } from "@/app/hooks/useAffiliateTracking"
import { useAffiliateSettingsSync } from "@/app/hooks/useAffiliateSettingsSync"
import { useAnalytics } from "@/app/hooks/useAnalytics"
import { useTelemetry } from "@/shared/telemetry/useTelemetry"
import { useAppWatchdog } from "@/shared/telemetry/useAppWatchdog"
import { useViewSeenStore } from "@/features/views/stores/viewSeenStore"
import { appLifecycleHub } from "@/shared/utils/platform/appLifecycleHub"
import { usePendingCompletions } from "@/features/tasks/stores/pendingCompletions"
import { initializeAnalytics, logAnalyticsEvent } from "@/shared/services/analytics"
import { trackAppStartup } from "@/shared/services/analyticsTrackers"
import { Capacitor } from "@capacitor/core"
import {
  backHandlerStack,
  BACK_PRIORITY,
} from "@/shared/utils/platform/backHandlerStack"

export function DeferredHooks() {
  useDeviceSync()
  useReminderSync()
  useSharedTaskDateSync()
  useSharedReminderSync()
  usePushSync()
  usePostLoginCleanup()
  useTokenSync()
  useAffiliateTracking()
  useAffiliateSettingsSync()
  // Calendar refresh moved to Today/Upcoming views (Fas 3b — on-demand)
  useAnalytics()
  useTelemetry()
  useAppWatchdog()

  // Hydrate view-seen timestamps from IndexedDB
  useEffect(() => {
    void useViewSeenStore.getState().hydrate()
  }, [])

  // Initialize AppLifecycleHub
  useEffect(() => {
    void appLifecycleHub.init()

    // Flush pending completions when app goes to background (v1 pattern)
    const removeBackground = appLifecycleHub.on("background", () => {
      void usePendingCompletions.getState().flushAll()
    })

    return () => {
      removeBackground()
      appLifecycleHub.destroy()
    }
  }, [])

  // Initialize Android back handler stack
  useEffect(() => {
    void backHandlerStack.init()

    const unregisterRoot = backHandlerStack.register(
      BACK_PRIORITY.ROOT_SCREEN,
      () => {
        void import("@capacitor/app").then(({ App }) => App.minimizeApp())
        return true
      },
      "root-minimize",
    )

    return () => {
      unregisterRoot()
      backHandlerStack.destroy()
    }
  }, [])

  // Initialize Firebase Analytics + track startup time
  useEffect(() => {
    const startupMs = Math.round(performance.now())
    void (async () => {
      await initializeAnalytics()
      await logAnalyticsEvent("app_open", {
        platform: Capacitor.isNativePlatform()
          ? Capacitor.getPlatform()
          : "web",
      })
      trackAppStartup(startupMs)
    })()
  }, [])

  return null
}
